<template>
    <SignaloDialog
        :open="open"
        :title="
            currentlyEditedCategory?.id === undefined
                ? $t('common.addCategory')
                : $t('common.editCategory')
        "
        @close="$emit('close')"
        :constHeight="false"
    >
        <template #body>
            <div v-if="currentlyEditedCategory">
                <v-form ref="categoryDataForm">
                    <SignaloTranslatableTabs
                        v-slot="{ value, update }"
                        v-model="currentlyEditedCategory.name"
                        :currentLanguage="$i18n?.locale"
                        :availableLocales="getAvailableLocales()"
                    >
                        <v-card-text>
                            <v-text-field
                                :label="$t('common.name')"
                                :modelValue="value"
                                @update:modelValue="update"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                required
                                class="required-field"
                                hide-details
                                single-line
                            >
                            </v-text-field>
                        </v-card-text>
                    </SignaloTranslatableTabs>
                    <v-card-text class="category-form-inputs">
                        <SignaloCategoryPicker
                            v-if="newCategoryFromSelect"
                            v-model="parentId"
                            v-slot="props"
                        >
                            <v-text-field
                                variant="outlined"
                                color="var(--greyscale-100)"
                                :modelValue="props.text"
                                @click:control="props.onClick"
                                @click:clear="props.onClear"
                                readonly
                                clearable
                                :label="$t('common.parentCategory')"
                            >
                            </v-text-field>
                        </SignaloCategoryPicker>
                        <SignaloIconPicker
                            v-model="icon"
                            :required="true"
                            :errorMessages="iconErrorMessage"
                        />
                        <div
                            class="category-color-container category-color-container-inherited"
                            v-if="
                                currentlyEditedCategory.color_inherited &&
                                currentlyEditedCategory.color === null
                            "
                        >
                            <SignaloColorPicker
                                label="common.colorInherited"
                                :disabled="true"
                                :modelValue="
                                    currentlyEditedCategory.color_inherited
                                "
                            />
                        </div>
                        <div class="category-color-container">
                            <SignaloColorPicker
                                :modelValue="currentlyEditedCategory.color"
                                @update:modelValue="updateColorHandler"
                            />
                        </div>
                        <v-checkbox
                            v-model="currentlyEditedCategory.can_be_picked"
                            hide-details
                            color="primary"
                            :label="$t('common.canBePicked')"
                        />

                        <v-checkbox
                            color="primary"
                            v-model="currentlyEditedCategory.planned"
                            :label="$t('common.planned')"
                            hide-details
                            single-line
                        ></v-checkbox>
                        <v-checkbox
                            color="primary"
                            v-model="
                                currentlyEditedCategory.formal_confirm_required
                            "
                            :label="$t('issues.formalConfirmationRequired')"
                            hide-details
                            single-line
                        ></v-checkbox>
                        <v-checkbox
                            color="primary"
                            v-model="
                                currentlyEditedCategory.issue_generates_cost
                            "
                            :label="$t('common.issueGeneratesCost')"
                            hide-details
                            single-line
                        ></v-checkbox>

                        <v-switch
                            v-model="postFinish"
                            color="primary"
                            :label="$t('common.actionAfterIssueFinish')"
                        ></v-switch>
                    </v-card-text>
                    <v-card-actions>
                        <v-tabs color="primary" v-model="tab" v-if="postFinish">
                            <v-tab value="postAction">{{
                                $t('common.actionAfterIssueFinish')
                            }}</v-tab></v-tabs
                        >
                    </v-card-actions>
                    <v-window v-model="tab" :touch="false" v-if="postFinish">
                        <v-window-item value="postAction">
                            <CategoryPostFinish
                                :modelValue="
                                    currentlyEditedCategory.post_finish
                                "
                                v-model="currentlyEditedCategory.post_finish"
                            />
                        </v-window-item>
                    </v-window>
                </v-form>
            </div>
        </template>
        <template #buttons>
            <v-btn text @click="$emit('close')">
                {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
                variant="elevated"
                @click="saveCategory"
                color="primary"
                :loading="saveLoading"
                :disabled="
                    !icon ||
                    !authStore.tenant.supportedLocales.every(
                        (al) => !!currentlyEditedCategory?.name?.[al]
                    ) ||
                    (postFinish
                        ? !currentlyEditedCategory?.post_finish?.rrule ||
                          !currentlyEditedCategory?.post_finish?.categoryPath
                        : false)
                "
            >
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </SignaloDialog>
</template>

<script>
import * as AUTH_STORE from 'signalo-vue-auth-store';
import * as DATA_STORE from 'signalo-vue-data-store';
import * as SIGNALO_VUE_HELPERS_STORE from 'signalo-vue-helpers-store';
import CategoryPostFinish from './CategoryPostFinish.vue';
export default {
    components: {
        CategoryPostFinish,
    },
    setup() {
        const authStore = AUTH_STORE.default();
        const dataStore = DATA_STORE.default();
        const helpersStore = SIGNALO_VUE_HELPERS_STORE.default();
        return { authStore, dataStore, helpersStore };
    },
    emits: ['close'],
    props: ['open', 'category', 'currentId', 'newCategoryFromSelect'],
    data() {
        return {
            saveLoading: false,
            currentlyEditedCategory: { ...this.category },
            editCategoryFormValid: false,
            icon: this.category?.icon || null,
            colorPickerOpened: false,
            iconErrorMessage: {},
            tab: 'postAction',
            postFinish: !!this.category?.post_finish,
            parentId: null,
        };
    },
    watch: {
        icon() {
            this.iconErrorMessage = [];
        },
    },
    methods: {
        async saveCategory() {
            this.iconErrorMessage = {};
            if (!this.icon) {
                this.iconErrorMessage = this.$t('common.fieldRequired');
                return;
            }
            this.saveLoading = true;
            this.currentlyEditedCategory.icon = this.icon;
            if (!this.postFinish) {
                this.currentlyEditedCategory.post_finish = null;
            }
            if (this.currentlyEditedCategory?.id) {
                this.axios
                    .put(
                        `/categories/${this.currentlyEditedCategory.id}`,
                        this.currentlyEditedCategory
                    )
                    .then(() => {
                        return this.dataStore.categories.reload();
                    })
                    .then(() => {
                        this.helpersStore.snackbar(
                            this.$t('common.updated'),
                            'success'
                        );
                        this.$emit('close');
                    })
                    .catch((e) => {
                        if (e?.response?.status == 401) return;
                        if (
                            e?.response?.status === 422 &&
                            e?.response?.data?.message?.includes(
                                'The icon has already been taken.'
                            )
                        ) {
                            this.iconErrorMessage = this.$t(
                                'errors.iconAlreadyTaken'
                            );
                        }
                    })
                    .finally(() => {
                        this.saveLoading = false;
                    });
            } else {
                if (this.currentId !== 0) {
                    this.currentlyEditedCategory.parent_id =
                        this.currentId || this.parentId;
                }
                let newCategory;
                this.axios
                    .post(`/categories`, this.currentlyEditedCategory)
                    .then((res) => {
                        newCategory = res?.data?.item;
                        return this.dataStore.categories.reload();
                    })
                    .then(() => {
                        this.helpersStore.snackbarSaved();
                        this.$emit('close', true, newCategory);
                    })
                    .catch((e) => {
                        if (e?.response?.status == 401) return;
                        if (
                            e?.response?.status === 422 &&
                            e?.response?.data?.message?.includes(
                                'The icon has already been taken.'
                            )
                        ) {
                            this.iconErrorMessage = this.$t(
                                'errors.iconAlreadyTaken'
                            );
                        }
                    })
                    .finally(() => {
                        this.saveLoading = false;
                    });
            }
        },
        updateColorHandler(value) {
            this.currentlyEditedCategory.color = value;
        },
    },
};
</script>

<style scoped>
.category-color-container-inherited .v-input {
    min-width: 160px;
}
.category-color-container-inherited {
    cursor: not-allowed !important;
}
.category-color-container-inherited .v-field--disabled {
    opacity: 1 !important;
}
</style>
